import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Linking,
  Alert,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import * as StripeService from '../services/stripeService';
import { updateUser as updateUserSupabase } from '../services/database';
import { colors, typography, spacing, borderRadius, shadows } from '../styles/theme';

const StripeConnectSetup = ({ clubId, clubName, email, stripeAccountId, stripeOnboardingComplete, onUpdate }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null);

  useEffect(() => {
    if (stripeAccountId && !stripeOnboardingComplete) {
      checkAccountStatus();
    }
  }, [stripeAccountId]);

  const checkAccountStatus = async () => {
    if (!stripeAccountId) return;

    try {
      setCheckingStatus(true);
      const status = await StripeService.checkClubStripeStatus(clubId);
      setAccountStatus(status);

      if (status.chargesEnabled && status.payoutsEnabled) {
        // Update database
        await updateStripeStatus(true);
        showToast('Stripe account is ready to accept payments!', 'success');
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error checking account status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const updateStripeStatus = async (onboardingComplete) => {
    try {
      await updateUserSupabase(clubId, {
        stripe_onboarding_complete: onboardingComplete,
      });
    } catch (error) {
      console.error('Error updating Stripe status:', error);
    }
  };

  const handleConnectStripe = async () => {
    try {
      setLoading(true);

      let accountId = stripeAccountId;
      let onboardingUrl;

      if (!accountId) {
        // Create new Connect account
        const result = await StripeService.createConnectAccount(clubId, email, clubName);
        accountId = result.accountId;
        onboardingUrl = result.onboardingUrl;
      } else {
        // Get account link for existing account
        onboardingUrl = await StripeService.getAccountLink(accountId, 'account_onboarding');
      }

      // Open onboarding URL
      const canOpen = await Linking.canOpenURL(onboardingUrl);
      if (canOpen) {
        await Linking.openURL(onboardingUrl);
        showToast('Complete Stripe onboarding in your browser', 'info');
      } else {
        Alert.alert(
          'Unable to open link',
          `Please visit: ${onboardingUrl}`,
          [{ text: 'Copy URL', onPress: () => {} }, { text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      showToast(error.message || 'Failed to connect Stripe account', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    try {
      if (!stripeAccountId) {
        showToast('No Stripe account found', 'error');
        return;
      }

      const dashboardUrl = await StripeService.getAccountLink(stripeAccountId, 'account_update');
      const canOpen = await Linking.canOpenURL(dashboardUrl);
      if (canOpen) {
        await Linking.openURL(dashboardUrl);
      } else {
        Alert.alert('Unable to open link', `Please visit: ${dashboardUrl}`);
      }
    } catch (error) {
      console.error('Error opening Stripe dashboard:', error);
      showToast('Failed to open Stripe dashboard', 'error');
    }
  };

  if (stripeOnboardingComplete) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <Icon name="check-circle" size={48} color="#4CAF50" />
          <Text style={styles.successTitle}>Stripe Connected</Text>
          <Text style={styles.successText}>
            Your account is ready to accept payments for events and tournaments.
          </Text>
          <TouchableOpacity style={styles.dashboardButton} onPress={handleViewDashboard}>
            <Icon name="dashboard" size={20} color="white" />
            <Text style={styles.dashboardButtonText}>View Stripe Dashboard</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const showStatusWarning = accountStatus && !accountStatus.chargesEnabled;

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Icon name="payment" size={32} color={colors.primary?.main || '#94EB98'} />
        <Text style={styles.title}>Accept Payments</Text>
      </View>

      <Text style={styles.description}>
        Connect your Stripe account to accept payments for events and tournaments. You'll receive
        payments directly to your bank account, and we take a small platform fee (default 10%).
      </Text>

      {showStatusWarning && (
        <View style={styles.warningContainer}>
          <Icon name="warning" size={20} color="#FF9800" />
          <Text style={styles.warningText}>
            Please complete the onboarding process to start accepting payments.
          </Text>
        </View>
      )}

      {stripeAccountId && (
        <View style={styles.statusContainer}>
          <Text style={styles.statusLabel}>Account Status:</Text>
          {checkingStatus ? (
            <ActivityIndicator size="small" color={colors.primary?.main || '#94EB98'} />
          ) : (
            <>
              <View style={styles.statusRow}>
                <Text style={styles.statusText}>
                  Charges: {accountStatus?.chargesEnabled ? '✓ Enabled' : '✗ Not enabled'}
                </Text>
              </View>
              <View style={styles.statusRow}>
                <Text style={styles.statusText}>
                  Payouts: {accountStatus?.payoutsEnabled ? '✓ Enabled' : '✗ Not enabled'}
                </Text>
              </View>
            </>
          )}
        </View>
      )}

      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={[styles.connectButton, loading && styles.connectButtonDisabled]}
          onPress={handleConnectStripe}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="white" />
          ) : (
            <>
              <Icon name="link" size={20} color="white" />
              <Text style={styles.connectButtonText}>
                {stripeAccountId ? 'Complete Setup' : 'Connect Stripe Account'}
              </Text>
            </>
          )}
        </TouchableOpacity>

        {stripeAccountId && (
          <TouchableOpacity style={styles.checkButton} onPress={checkAccountStatus}>
            <Text style={styles.checkButtonText}>Check Status</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={styles.infoContainer}>
        <Text style={styles.infoTitle}>How it works:</Text>
        <Text style={styles.infoText}>• Complete Stripe onboarding (takes ~5 minutes)</Text>
        <Text style={styles.infoText}>• Provide your business information</Text>
        <Text style={styles.infoText}>• Add your bank account for payouts</Text>
        <Text style={styles.infoText}>• Start accepting payments for events!</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    margin: spacing.md,
    ...shadows.card,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  title: {
    ...typography.h3,
    color: colors.text?.primary || '#333',
  },
  description: {
    ...typography.body,
    color: colors.text?.secondary || '#666',
    marginBottom: spacing.md,
    lineHeight: 20,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFF3E0',
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    gap: spacing.xs,
  },
  warningText: {
    ...typography.caption,
    color: '#FF6F00',
    flex: 1,
  },
  statusContainer: {
    backgroundColor: colors.background?.secondary || '#f5f5f5',
    padding: spacing.md,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  statusLabel: {
    ...typography.body,
    fontWeight: '600',
    marginBottom: spacing.xs,
  },
  statusRow: {
    marginTop: spacing.xs,
  },
  statusText: {
    ...typography.body,
    color: colors.text?.primary || '#333',
  },
  buttonContainer: {
    gap: spacing.sm,
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary?.main || '#94EB98',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    gap: spacing.sm,
  },
  connectButtonDisabled: {
    opacity: 0.6,
  },
  connectButtonText: {
    ...typography.button,
    color: 'white',
    fontWeight: '600',
  },
  checkButton: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  checkButtonText: {
    ...typography.body,
    color: colors.primary?.main || '#94EB98',
  },
  successContainer: {
    alignItems: 'center',
    padding: spacing.lg,
  },
  successTitle: {
    ...typography.h3,
    color: colors.text?.primary || '#333',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
  },
  successText: {
    ...typography.body,
    color: colors.text?.secondary || '#666',
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  dashboardButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary?.main || '#94EB98',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderRadius: borderRadius.md,
    gap: spacing.sm,
  },
  dashboardButtonText: {
    ...typography.button,
    color: 'white',
    fontWeight: '600',
  },
  infoContainer: {
    marginTop: spacing.lg,
    paddingTop: spacing.md,
    borderTopWidth: 1,
    borderTopColor: colors.border || '#e0e0e0',
  },
  infoTitle: {
    ...typography.body,
    fontWeight: '600',
    marginBottom: spacing.xs,
  },
  infoText: {
    ...typography.caption,
    color: colors.text?.secondary || '#666',
    marginTop: spacing.xs,
  },
});

export default StripeConnectSetup;
